import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Container, Row, Col, Form, Button, ButtonGroup,
  OverlayTrigger, Tooltip, Modal, Offcanvas, Accordion, Badge, Spinner
} from "react-bootstrap";
import { getUserPracticeById } from "../../api";
import { getSalesReportByPracticeNumbers } from "../../afinanceapi";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { unparse } from "papaparse";
import "./CreateInvoice.css"; // Assuming you have some styles in this file

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function TransactionList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Data for dropdowns
  const [practiceList, setPracticeList] = useState([]);

  // Committed filters (drive the table)
  const [selectedPractices, setSelectedPractices] = useState([]); // [] = All
  const [selectedStatuses, setSelectedStatuses] = useState([]);   // [] = All

  // Committed dates (default 1 month ago → today)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Temporary filters while panel is open
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpStatuses, setTmpStatuses] = useState([]);
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 20;

  // UI
  const [showInfo, setShowInfo] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();
  const query = useQuery();

  const currency = new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" });

  // Init dates once
 useEffect(() => {
  const today = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7); // Changed from setMonth to setDate
  const iso = (d) => d.toISOString().slice(0, 10);
  setStartDate(iso(start));
  setEndDate(iso(today));
  setTmpStartDate(iso(start));
  setTmpEndDate(iso(today));
}, []);

  // Fetch practices
  useEffect(() => {
    async function fetchPractices() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");
        const user = jwtDecode(token);
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);
      } catch (e) {
        console.error("Error fetching practices:", e);
        setPracticeList([]);
      }
    }
    fetchPractices();
  }, []);

  // Fetch data when dates or practices change
  useEffect(() => {
    if (!startDate || !endDate || practiceList.length === 0) return;

    async function fetchInvoices() {
      try {
        setLoading(true);
        const practiceNums = practiceList.map((p) => p.practiceNumber);
        const data = await getSalesReportByPracticeNumbers(
          practiceNums,
          startDate,
          endDate
        );
        setInvoices(data || []);
      } catch (e) {
        console.error("Error fetching invoices:", e);
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    }
    fetchInvoices();
  }, [startDate, endDate, practiceList]);

  // Dynamic statuses
  const statusOptions = useMemo(() => {
    const set = new Set(invoices.map((i) => i.status).filter(Boolean));
    return Array.from(set).sort();
  }, [invoices]);

  // Helpers for multi-select toggles
  const toggleIn = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const allPractices = useMemo(
    () => (practiceList || []).map((p) => p.practiceName).filter(Boolean),
    [practiceList]
  );

  // Filtered (entire dataset)
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const practiceName = inv.practice?.practiceName;
      const practiceOK =
        selectedPractices.length === 0 || (practiceName && selectedPractices.includes(practiceName));

      const statusOK =
        selectedStatuses.length === 0 || (inv.status && selectedStatuses.includes(inv.status));

      return practiceOK && statusOK;
    });
  }, [invoices, selectedPractices, selectedStatuses]);

  // Create display rows (invoices + credit notes)
  const displayRows = useMemo(() => {
    return filteredInvoices.reduce((acc, inv) => {
      // push the invoice row
      acc.push({ type: "invoice", inv });
      // then push each credit‐note row
      (inv.receipts || [])
        .filter((r) => r.creditNoteNumber)
        .forEach((r) => acc.push({ type: "credit", inv, r }));
      return acc;
    }, []);
  }, [filteredInvoices]);

  // Sort the display rows
  const sortedDisplayRows = useMemo(() => {
    if (!sortConfig.key) return displayRows;
    const arr = [...displayRows];
    arr.sort((a, b) => {
      const valA = (a.type === 'invoice' ? a.inv[sortConfig.key] : a.inv[sortConfig.key]) ?? "";
      const valB = (b.type === 'invoice' ? b.inv[sortConfig.key] : b.inv[sortConfig.key]) ?? "";
      if (typeof valA === "string") {
        return sortConfig.direction === "asc"
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      }
      if (typeof valA === "number") {
        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }
      return 0;
    });
    return arr;
  }, [displayRows, sortConfig]);

  // Compute totals for entire filtered dataset
  const totals = useMemo(() => {
    // 1️⃣ Regular sums
    const sumExclInv = filteredInvoices.reduce(
      (sum, inv) => sum + (inv.exclAmount || 0),
      0
    );
    const sumVatInv = filteredInvoices.reduce(
      (sum, inv) => sum + (inv.vatAmount || 0),
      0
    );
    const sumInvTotals = filteredInvoices.reduce(
      (sum, inv) => sum + (inv.totalAmount || 0),
      0
    );

    // 2️⃣ Pull out all credit-note amounts
    const totalCreditNotes = filteredInvoices.reduce((sum, inv) => {
      const creditsOnThisInv = (inv.receipts || []).reduce(
        (s, r) => s + (r.creditNoteAmount || 0),
        0
      );
      return sum + creditsOnThisInv;
    }, 0);

    const totalCreditNoteVAT = filteredInvoices.reduce((sum, inv) => {
      const totalCreditNoteVAT = (inv.receipts || []).reduce(
        (s, r) => s + (r.creditNoteVATAmount || 0),
        0
      );
      return sum + totalCreditNoteVAT;
    }, 0);

    const totalCreditNoteExcl = filteredInvoices.reduce((sum, inv) => {
      const totalCreditNoteExcl = (inv.receipts || []).reduce(
        (s, r) => s + (r.creditNoteExclAmount || 0),
        0
      );
      return sum + totalCreditNoteExcl;
    }, 0);

    // 3️⃣ Subtract credit-notes from the overall total
    const sumTotal = sumInvTotals - totalCreditNotes;
    const sumVat = sumVatInv - totalCreditNoteVAT;
    const sumExcl = sumExclInv - totalCreditNoteExcl;

    return { sumExcl, sumVat, sumTotal };
  }, [filteredInvoices]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedDisplayRows.length / limit));
  const pageStart = (page - 1) * limit;
  const pagedRows = sortedDisplayRows.slice(pageStart, pageStart + limit);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [selectedPractices, selectedStatuses, startDate, endDate]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="fa-solid fa-arrows-up-down text-primary ms-2" />;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  const handleExportCSV = () => {
    const exportData = filteredInvoices.map((inv) => ({
      InvoiceDate: inv.invoiceDate,
      InvoiceNumber: inv.invoiceNumber,
      PracticeName: inv.practice?.practiceName || "",
      AccountName: inv.accountName,
      Status: inv.status,
      ExclAmount: inv.exclAmount || 0,
      VatAmount: inv.vatAmount || 0,
      TotalAmount: inv.totalAmount || 0,
    }));
    const csv = unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transaction-report.csv";
    a.click();
  };

  // Open filter panel with current committed values loaded into temp
  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpStatuses(selectedStatuses);
    setTmpStartDate(startDate);
    setTmpEndDate(endDate);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedStatuses(tmpStatuses);
    setStartDate(tmpStartDate);
    setEndDate(tmpEndDate);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedStatuses([]);
    // Reset to default date range (last month → today)
    const today = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    const s = iso(start);
    const e = iso(today);
    setStartDate(s);
    setEndDate(e);

    // Also reset temp in case panel is open
    setTmpPractices([]);
    setTmpStatuses([]);
    setTmpStartDate(s);
    setTmpEndDate(e);
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedStatuses.length ? 1 : 0) +
    ((startDate && endDate) ? 1 : 0);

  return (
    <Container fluid className="pt-2">
      {/* Single top bar: Filter + Remove + Pagination + CSV + Share/Info (right-aligned) */}
      <Row className="mb-3 align-items-center">
        {/* Right Column: Totals Table */}
        <Col md={5}>
        <span className="text-primary fw-semibold">Total Transactions:</span>
          <Table bordered size="sm" className="mb-0 text-center align-middle">
            <thead className="table-primary">
              <tr>
                <th style={{ width: "33%" }} className="fw-semibold text-primary">Excluding VAT</th>
                <th style={{ width: "33%" }} className="fw-semibold text-primary">Including VAT</th>
                <th style={{ width: "34%" }} className="fw-semibold text-primary">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{currency.format(totals.sumExcl)}</td>
                <td>{currency.format(totals.sumVat)}</td>
                <td><strong>{currency.format(totals.sumTotal)}</strong></td>
              </tr>
            </tbody>
          </Table>
        </Col>
        {/* Left Column: Filter + Pagination + Actions */}
        <Col md={7} className="d-flex gap-2 flex-wrap justify-content-end">
          <Button variant="primary" onClick={openFilters} style={{ width: "120px" }}>
            <i className="fa-solid fa-filter me-2" />
            Filter {activeCount > 0 && <Badge className="ms-2" bg="light" text="dark">{activeCount}</Badge>}
          </Button>
          <Button
            variant="outline-danger"
            onClick={clearFilters}
            style={{ width: "120px" }}
            disabled={
              selectedPractices.length === 0 &&
              selectedStatuses.length === 0 &&
              !startDate &&
              !endDate
            }
          >
            <i className="fa-solid fa-trash me-2" />
            Filters
          </Button>

          {/* Pagination */}
          <div className="d-flex align-items-center gap-2 ms-2">
            <Button
              variant="primary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fa-solid fa-angle-left" />
            </Button>
            <div className="text-nowrap small">
              Page {page} of {totalPages}
            </div>
            <Button
              variant="primary"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fa-solid fa-angle-right" />
            </Button>
          </div>

          {/* Export + Info */}
          <ButtonGroup className="ms-1 gap-1">
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Export to CSV</Tooltip>}>
              <Button variant="outline-secondary" onClick={handleExportCSV}>
                <i className="fa-solid fa-file-export" />
              </Button>
            </OverlayTrigger>
            
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Info</Tooltip>}>
              <Button variant="outline-secondary" onClick={() => setShowInfo(true)}>
                <i className="fa-solid fa-circle-info" />
              </Button>
            </OverlayTrigger>
          </ButtonGroup>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading Transactions...</p>
          </div>
      ) : pagedRows.length === 0 ? (
        <p>No transactions found. The default filter is for the last 7 days.</p>
      ) : (
        <Table bordered hover responsive className="fixed-table">
          <thead className="table-primary text-center align-middle ms-1 me-1">
            <tr>
              <th style={{ width: "2%" }} className="text-primary fw-semibold">#</th>
              <th style={{ width: "15%" }} className="text-primary fw-semibold">Date</th>
              <th style={{ width: "10%" }} className="text-primary fw-semibold">Inv Number</th>
              <th style={{ width: "16%" }} className="text-primary fw-semibold">Practice Name</th>
              <th
                style={{ cursor: "pointer", width: "18%" }}
                onClick={() => handleSort("accountName")}
                className="text-primary fw-semibold"
              >
                Account Name {renderSortIcon("accountName")}
              </th>
              <th
                style={{ cursor: "pointer", width: "8%" }}
                onClick={() => handleSort("status")}
                className="text-primary fw-semibold"
              >
                Status {renderSortIcon("status")}
              </th>
              <th style={{ width: "10%" }} className="text-primary fw-semibold">*Excl Amount</th>
              <th style={{ width: "10%" }} className="text-primary fw-semibold">*VAT Amount</th>
              <th style={{ width: "10%" }} className="text-primary fw-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, idx) => {
              const rowNum = (page - 1) * limit + idx + 1;

              if (row.type === "invoice") {
                const inv = row.inv;
                return (
                  <tr
                    key={`inv-${inv._id}`}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(
                        inv.status === "Pending"
                          ? `/invoices/${inv._id}/${inv.accountId}`
                          : `/invoices/posted/${inv._id}`
                      )
                    }
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6c757d")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                  >
                    <td>{rowNum}</td>
                    <td>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : ""}</td>
                    <td>{inv.invoiceNumber}</td>
                    <td>{inv.practice?.practiceName}</td>
                    <td
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/accounts/${inv.accountId}`);
                      }}
                    >
                      {inv.accountName}
                    </td>
                    <td>{inv.status}</td>
                    <td className="text-end">
                      {inv.exclAmount ? currency.format(inv.exclAmount) : "-"}
                    </td>
                    <td className="text-end">
                      {inv.vatAmount ? currency.format(inv.vatAmount) : "-"}
                    </td>
                    <td className="text-end fw-bold">
                      {currency.format(inv.totalAmount || 0)}
                    </td>
                  </tr>
                );
              } else {
                // credit‐note row
                const { inv, r } = row;
                return (
                  <tr key={`inv-${inv._id}-cn-${r.creditNoteNumber}`}>
                    <td>{rowNum}</td>
                    <td>{r.creditNoteDate}</td>
                    <td>{r.creditNoteNumber}</td>
                    <td>{inv.practice?.practiceName}</td>
                    <td>{inv.accountName}</td>
                    <td>Credit Note</td>
                    <td className="text-end">
                      ({currency.format(r.creditNoteExclAmount || 0)})
                    </td>
                    <td className="text-end">
                      ({currency.format(r.creditNoteVATAmount || 0)})
                    </td>
                    <td className="text-end">
                      ({currency.format(r.creditNoteAmount || 0)})
                    </td>
                  </tr>
                );
              }
            })}
          </tbody>
        </Table>
      )}

      {/* Info modal */}
      <Modal show={showInfo} onHide={() => setShowInfo(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Transaction Report</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted">
          Use the filter panel to refine the dataset by date range, practice, and status. 
          Totals reflect all filtered rows (not just this page). CSV export downloads the entire filtered dataset.
          Credit notes are shown as negative amounts in brackets. <strong>*Excluding</strong> and <strong>*VAT</strong> amounts are only populated for practices registered for VAT.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowInfo(false)}>Got it</Button>
        </Modal.Footer>
      </Modal>

      {/* Filter Offcanvas */}
      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="test-primary d-flex align-items-center">
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {/* Dates */}
          <div className="mb-3">
            <h6 className="mb-1 text-primary fw-normal ms-3 mb-2">Date range</h6>
            <div className="d-flex gap-2 ms-1">
              <Form.Control
                type="date"
                value={tmpStartDate}
                onChange={(e) => setTmpStartDate(e.target.value)}
              />
              <Form.Control
                type="date"
                value={tmpEndDate}
                onChange={(e) => setTmpEndDate(e.target.value)}
              />
            </div>
          </div>

          <Accordion alwaysOpen>
            {/* Practices */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices(allPractices)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices([])}
                  >
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column" style={{ maxHeight: 240, overflowY: "auto" }}>
                  {allPractices.map((name) => (
                    <Form.Check
                      key={name}
                      type="checkbox"
                      id={`practice-${name}`}
                      label={name}
                      checked={tmpPractices.includes(name)}
                      onChange={() => setTmpPractices((arr) => toggleIn(arr, name))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Statuses */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Status</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpStatuses(statusOptions)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpStatuses([])}
                  >
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column">
                  {statusOptions.map((s) => (
                    <Form.Check
                      key={s}
                      type="checkbox"
                      id={`status-${s}`}
                      label={s}
                      checked={tmpStatuses.includes(s)}
                      onChange={() => setTmpStatuses((arr) => toggleIn(arr, s))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Actions */}
          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              style={{ width: "120px" }}
              variant="outline-danger"
              onClick={() => {
                setTmpPractices([]);
                setTmpStatuses([]);
                setTmpStartDate("");
                setTmpEndDate("");
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Remove
            </Button>
          </div>
          <div className="mt-2 text-muted small">
            Tip: leaving a section empty means "All".
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}